/**
 * Pure logic for subscription state, trial countdown banners, and gated
 * feature checks. Kept free of Firebase imports so it can be unit-tested
 * with vitest.
 *
 * Implements the client portion of
 * [[specs/stripe-billing-and-marketing-site-portal]] and the trial-to-paid
 * soft-lockout behavior from [[specs/monetization-and-beta]].
 */

import type {
  HouseholdSubscription,
  SubscriptionSource,
  SubscriptionTier,
} from "@/lib/models";

/**
 * Origin of the marketing site (where /pricing, /billing, /checkout/* live).
 * Overridden at build time via `NEXT_PUBLIC_MARKETING_ORIGIN` for staging.
 */
export const MARKETING_ORIGIN: string =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.NEXT_PUBLIC_MARKETING_ORIGIN) ||
  "https://zoeyorganizer.com";

/**
 * Minimal Timestamp shape — matches Firebase `Timestamp` and the objects
 * returned by `onSnapshot` but keeps this module Firebase-free.
 */
export interface TimestampLike {
  toDate?: () => Date;
  seconds?: number;
}

export function tsToDate(
  ts: TimestampLike | Date | null | undefined,
): Date | null {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts.toDate === "function") {
    try {
      return ts.toDate();
    } catch {
      /* fall through */
    }
  }
  if (typeof ts.seconds === "number") {
    return new Date(ts.seconds * 1000);
  }
  return null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Integer days remaining until `expires_at`. Negative if already expired. */
export function daysUntil(
  expiresAt: TimestampLike | Date | null | undefined,
  now: Date = new Date(),
): number | null {
  const d = tsToDate(expiresAt);
  if (!d) return null;
  return Math.ceil((d.getTime() - now.getTime()) / MS_PER_DAY);
}

/** Paid tiers that unlock every AI feature. */
const FULL_AI_TIERS: ReadonlyArray<SubscriptionTier> = [
  "zoey_plus",
  "zoey_family",
  "founders_beta",
];

/** Starter tier unlocks a *subset* of AI (dinner meal suggestions only). */
const STARTER_TIERS: ReadonlyArray<SubscriptionTier> = ["zoey_starter"];

/**
 * Gated features per [[specs/monetization-and-beta]] feature matrix.
 * `starter` → available on Zoey Starter and above.
 * `plus` → requires Zoey+ or Zoey Family (or founders beta).
 */
export type GatedFeature =
  | "ai_meal_dinner"
  | "ai_meal_breakfast_lunch"
  | "weekend_plans"
  | "receipt_scanning"
  | "packed_lunch";

const PLUS_FEATURES: ReadonlyArray<GatedFeature> = [
  "ai_meal_breakfast_lunch",
  "weekend_plans",
  "receipt_scanning",
  "packed_lunch",
];

export interface SubscriptionState {
  subscription: HouseholdSubscription | undefined;
  tier: SubscriptionTier;
  status: HouseholdSubscription["status"];
  /** Non-null, authoritative source of truth for "can they use AI". */
  isActive: boolean;
  /** Still inside a trial window (tier === "trial" AND not expired). */
  isTrial: boolean;
  /** Trial has ended → tier null / canceled. */
  isExpired: boolean;
  /** Subscription failed to renew — show dunning banner. */
  isPastDue: boolean;
  /** Integer days until trial ends. `null` if not in a trial / no expires_at. */
  daysUntilTrialEnd: number | null;
  /** True when no billing history exists (first time → paywall modal instead of Portal). */
  hasStripeCustomer: boolean;
  /**
   * Origin of the active subscription, copied verbatim from
   * `households/{hh}.subscription.source`. Drives source-aware
   * Manage-Billing routing: "stripe" → Customer Portal, "app_store" →
   * Apple's subscription management URL, "invite"/"promo" → no
   * self-serve management (contact support).
   */
  source: SubscriptionSource | null;
}

/**
 * Derive `SubscriptionState` from the Firestore subscription block.
 *
 * `hasStripeCustomer` is supplied externally (from the `stripe_customers/{uid}`
 * listener) because it lives on a separate doc. Defaults to inferring from
 * the `stripe_customer_id` field if not explicitly provided.
 */
export function deriveSubscriptionState(
  subscription: HouseholdSubscription | undefined,
  opts: { hasStripeCustomer?: boolean; now?: Date } = {},
): SubscriptionState {
  const now = opts.now ?? new Date();
  const tier = (subscription?.tier ?? null) as SubscriptionTier;
  const status = subscription?.status ?? null;
  const days = daysUntil(subscription?.expires_at, now);

  const isTrial =
    tier === "trial" &&
    status !== "canceled" &&
    status !== "past_due" &&
    (days ?? 1) > 0;
  const isPastDue = status === "past_due";
  const isActive =
    tier !== null &&
    tier !== undefined &&
    (status === "active" || status === "trialing" || isTrial);
  const isExpired =
    (tier === null || tier === undefined) &&
    (status === "canceled" || status === null);

  const hasStripeCustomer =
    opts.hasStripeCustomer ?? Boolean(subscription?.stripe_customer_id);

  return {
    subscription,
    tier,
    status,
    isActive,
    isTrial,
    isExpired,
    isPastDue,
    daysUntilTrialEnd: isTrial ? days : null,
    hasStripeCustomer,
    source: subscription?.source ?? null,
  };
}

/**
 * Where the "Manage Billing" UI should send the user given their current
 * subscription state. Three outcomes:
 *
 *   - `"apple"`  — outbound to https://apps.apple.com/account/subscriptions
 *                  (App Store-managed subs can't be touched via Stripe Portal)
 *   - `"stripe"` — open the Stripe Customer Portal via createBillingPortalSession
 *   - `"paywall"`— show the subscribe paywall (no Stripe-managed sub to manage)
 *
 * Trial users land in `"paywall"` because `isActive` is true (they have AI
 * access) but `stripe_customer_id` is null until a webhook confirms payment.
 * Abandoned-checkout users (Stripe customer record exists from
 * `ensureStripeCustomer` but never paid) also land in `"paywall"`.
 */
export type BillingDestination = "apple" | "stripe" | "paywall";

export function chooseBillingDestination(
  state: SubscriptionState,
): BillingDestination {
  if (state.source === "app_store") return "apple";
  if (state.subscription?.stripe_customer_id) return "stripe";
  return "paywall";
}

/** True if the given feature is usable under the current subscription state. */
export function isFeatureUnlocked(
  feature: GatedFeature,
  state: SubscriptionState,
): boolean {
  // Trial unlocks everything (full parity with Zoey+) until it ends.
  if (state.isTrial) return true;
  if (!state.isActive) return false;

  if (FULL_AI_TIERS.includes(state.tier)) return true;
  if (STARTER_TIERS.includes(state.tier)) {
    // Starter unlocks dinner-only meal suggestions, nothing else.
    return feature === "ai_meal_dinner";
  }
  // Trial handled above; all other tiers are locked.
  return !PLUS_FEATURES.includes(feature);
}

/**
 * Trial countdown banner variants. Computed client-side from `expires_at`
 * per the Q2 "trial-to-paid transition" decision.
 */
export type TrialBannerKind =
  | { kind: "none" }
  | { kind: "warning"; days: number } // amber, days ∈ [3..7]
  | { kind: "urgent"; days: number } // escalated amber, days ∈ [1..2]
  | { kind: "expired" }; // red persistent, trial has ended

/**
 * Compute which banner (if any) should show. Visible only for trial accounts
 * and expired-trial accounts. Paid subs return `{ kind: "none" }`.
 *
 *   Day 1-22: `{ kind: "none" }`
 *   Day 23 (7 days remaining) → "warning"
 *   Day 28 (2 days remaining) → "urgent"
 *   Day 30+ (expired)         → "expired"
 */
export function computeTrialBanner(
  state: SubscriptionState,
): TrialBannerKind {
  if (state.isPastDue) return { kind: "none" }; // dunning handles it
  if (state.isTrial) {
    const days = state.daysUntilTrialEnd;
    if (days == null) return { kind: "none" };
    if (days <= 2 && days > 0) return { kind: "urgent", days };
    if (days <= 7 && days > 0) return { kind: "warning", days };
    return { kind: "none" };
  }
  if (state.isExpired) return { kind: "expired" };
  return { kind: "none" };
}

/** Build a marketing-site URL with optional query string + handoff token. */
export function buildMarketingUrl(
  path: "/pricing" | "/billing" | string,
  token?: string,
): string {
  const base = `${MARKETING_ORIGIN}${path}`;
  if (!token) return base;
  const separator = base.includes("?") ? "&" : "?";
  return `${base}${separator}t=${encodeURIComponent(token)}`;
}

/** Human-readable label for the tier badge in the Account header. */
export function tierDisplayLabel(state: SubscriptionState): string {
  if (state.isPastDue) return "Past due";
  if (state.isTrial) {
    const d = state.daysUntilTrialEnd;
    if (d == null) return "Trial";
    if (d <= 0) return "Trial ended";
    return d === 1 ? "Trial — 1 day left" : `Trial — ${d} days left`;
  }
  if (state.isExpired) return "Free tier";
  switch (state.tier) {
    case "zoey_plus":
      return "Zoey+";
    case "zoey_family":
      return "Zoey Family";
    case "zoey_starter":
      return "Zoey Starter";
    case "founders_beta":
      return "Founders Beta";
    default:
      return "Free tier";
  }
}
